import { type UploadFile } from 'antd';
import {
  array,
  boolean,
  mixed,
  number,
  object,
  string,
  type InferType,
} from 'yup';
import type { UploadFileOutputDto } from '../file/model';
import {
  videoModelSpecs,
  type GenerationType,
  type VideoModelCode,
} from '../video-model/model-spec';
import {
  durationSchema,
  multiPromptSchema,
  simplePromptSchema,
} from '../video/schema';

const fileListSchema = array()
  .of(mixed<UploadFile<UploadFileOutputDto>>().required())
  .min(1, '至少提供一张参考图')
  .when(['$generation_type', 'model'], (values, schema) => {
    const [generation_type, model] = values as [GenerationType, VideoModelCode];
    const spec = videoModelSpecs.get(model);
    if (!spec) {
      return schema;
    }

    const modelName = videoModelSpecs.get(model)?.name ?? model;

    if (generation_type !== 'reference') {
      if (!spec.image_number?.frame) {
        return schema;
      }

      const { min, max } = spec.image_number.frame;

      let newSchema = schema;

      if (min) {
        newSchema = newSchema.min(min, `${modelName} 至少需要 ${min} 张图片`);
      }

      if (max) {
        newSchema = newSchema.max(max, `${modelName} 最多支持 ${max} 张图片`);
      }

      return newSchema;
    }

    if (!spec.image_number?.reference) {
      return schema;
    }

    const { min, max } = spec.image_number.reference;
    let newSchema = schema;

    if (min) {
      newSchema = newSchema.test({
        name: 'min',
        message: `${modelName} 至少需要 ${min} 张参考图`,
        test: (value) => {
          return number()
            .min(min)
            .isValidSync(value?.length ?? 0);
        },
      });
    }

    if (max) {
      newSchema = newSchema.test({
        name: 'max',
        message: `${modelName} 最多支持 ${max} 张参考图`,
        test: (value) => {
          return number()
            .max(max)
            .isValidSync(value?.length ?? 0);
        },
      });
    }

    return newSchema;
  })
  .test((values, ctx) => {
    if (!values?.length) {
      return true;
    }

    if (values.some((item) => item.status === 'uploading')) {
      return ctx.createError({
        message: '文件正在上传中，请稍等',
      });
    }

    if (values.every((item) => item.status !== 'done')) {
      return ctx.createError({
        message: '文件均上传失败，请重新上传',
      });
    }

    return true;
  })
  .when(['$generation_type', 'model'], (values, schema) => {
    const [generation_type, model] = values as [GenerationType, VideoModelCode];
    const spec = videoModelSpecs.get(model);
    if (!spec) {
      return schema;
    }

    if (generation_type !== 'reference') {
      return schema;
    }

    return schema.test({
      name: 'max',
      message: '最多支持 1 个参考视频',
      test: (value) => {
        const videos =
          value
            ?.filter((item) => item.type?.startsWith('video/mp4'))
            .filter((item) => item.status === 'done') ?? [];

        return videos.length <= 1;
      },
    });
  })
  .when(['$generation_type', 'model'], (values, schema) => {
    const [generation_type, model] = values as [GenerationType, VideoModelCode];
    const spec = videoModelSpecs.get(model);
    if (!spec) {
      return schema;
    }

    if (generation_type !== 'reference') {
      return schema;
    }

    const audio_number = spec.audio_number;

    if (!audio_number) {
      return schema.test({
        name: 'unsupported',
        message: `${spec.name}不支持参考音频`,
        test: (value) => {
          const audios =
            value
              ?.filter((item) => item.type?.startsWith('audio/'))
              .filter((item) => item.status === 'done') ?? [];

          return !audios.length;
        },
      });
    }

    return schema.test({
      name: 'max',
      message: `最多支持 ${audio_number} 个参考音频`,
      test: (value) => {
        const audios =
          value
            ?.filter((item) => item.type?.startsWith('audio/'))
            .filter((item) => item.status === 'done') ?? [];

        return audios.length <= audio_number;
      },
    });
  })
  .nullable()
  .required('请上传参考图');

export const schema = object({
  product_name: string().nullable().required(),
  model: string<VideoModelCode>().nullable().required(),
  aspect_ratio: string().nullable().required(),
  file_list: fileListSchema,
  duration: durationSchema,
  prompt: simplePromptSchema,
  multi_prompt: multiPromptSchema,
  extra_prompt: string().nullable(),
  avatar_ids: array().of(string().required()).nullable(),
  generate_audio: boolean().default(true),
});

export type FileListFormValues = InferType<typeof fileListSchema>;
